import * as THREE from "three";

export interface PlayerCallbacks {
  onInteract: () => void;
  onRespawn: () => void;
  onGlideChange: (gliding: boolean) => void;
  onPointerUnlock: () => void;
}

export interface PlayerWorldAccess {
  groundHeightAt: (x: number, z: number) => number | null;
  resolveMovement: (
    previous: THREE.Vector2,
    desired: THREE.Vector2,
    feetY: number,
  ) => THREE.Vector2;
  cameraColliders: readonly THREE.Object3D[];
}

interface LimbSet {
  leftArm: THREE.Group;
  rightArm: THREE.Group;
  leftLeg: THREE.Group;
  rightLeg: THREE.Group;
  scarf: THREE.Mesh;
  body: THREE.Group;
}

const UP = new THREE.Vector3(0, 1, 0);
const MIN_VIEW_PITCH = -0.18;
const MAX_VIEW_PITCH = 0.82;

export function applyVerticalLookDelta(
  currentPitch: number,
  deltaY: number,
  sensitivity: number,
): number {
  return THREE.MathUtils.clamp(
    currentPitch + deltaY * sensitivity,
    MIN_VIEW_PITCH,
    MAX_VIEW_PITCH,
  );
}

export class PlayerController {
  readonly object = new THREE.Group();

  private readonly camera: THREE.PerspectiveCamera;
  private readonly canvas: HTMLCanvasElement;
  private readonly world: PlayerWorldAccess;
  private readonly callbacks: PlayerCallbacks;
  private readonly velocity = new THREE.Vector3();
  private readonly checkpoint = new THREE.Vector3(0, 18, 28);
  private readonly keys = new Set<string>();
  private readonly moveInput = new THREE.Vector2();
  private readonly raycaster = new THREE.Raycaster();
  private readonly cameraTarget = new THREE.Vector3();
  private readonly cameraPosition = new THREE.Vector3();
  private readonly limbs: LimbSet;
  private readonly glider: THREE.Group;
  private enabled = false;
  private paused = true;
  private grounded = false;
  private gliding = false;
  private jumpQueued = false;
  private glidePressed = false;
  private lastGroundedAt = 0;
  private yaw = 0.2;
  private pitch = 0.34;
  private walkCycle = 0;
  private pointerWasLocked = false;
  private dragPointerId: number | null = null;
  private dragLastX = 0;
  private dragLastY = 0;

  constructor(
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    canvas: HTMLCanvasElement,
    world: PlayerWorldAccess,
    callbacks: PlayerCallbacks,
  ) {
    this.camera = camera;
    this.canvas = canvas;
    this.world = world;
    this.callbacks = callbacks;
    this.object.name = "雲海測繪員";
    this.limbs = this.createBlockCharacter();
    this.glider = this.createGlider();
    this.object.add(this.limbs.body, this.glider);
    scene.add(this.object);
    this.object.position.copy(this.checkpoint);
    this.bindInput();
    this.updateCamera(1, true);
  }

  get position(): THREE.Vector3 {
    return this.object.position;
  }

  get isGrounded(): boolean {
    return this.grounded;
  }

  get isGliding(): boolean {
    return this.gliding;
  }

  get viewYaw(): number {
    return this.yaw;
  }

  get viewPitch(): number {
    return this.pitch;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.paused = !enabled;
    if (!enabled) this.clearInput();
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
    if (paused) {
      this.clearInput();
      if (document.pointerLockElement === this.canvas) document.exitPointerLock();
    }
  }

  setCheckpoint(position: THREE.Vector3): void {
    const ground = this.world.groundHeightAt(position.x, position.z);
    this.checkpoint.set(position.x, ground === null ? position.y : ground + 0.08, position.z);
  }

  teleport(position: THREE.Vector3): void {
    const ground = this.world.groundHeightAt(position.x, position.z);
    this.object.position.set(position.x, ground === null ? position.y : Math.max(position.y, ground + 0.08), position.z);
    this.velocity.set(0, 0, 0);
    this.updateCamera(1, true);
  }

  requestPointerLock(): void {
    if (!this.enabled || this.paused || matchMedia("(pointer: coarse)").matches) return;
    const request = this.canvas.requestPointerLock();
    if (request instanceof Promise) request.catch(() => undefined);
  }

  setTouchMovement(x: number, y: number): void {
    this.moveInput.set(
      THREE.MathUtils.clamp(x, -1, 1),
      THREE.MathUtils.clamp(y, -1, 1),
    );
  }

  addTouchLook(deltaX: number, deltaY: number): void {
    if (!this.enabled || this.paused) return;
    this.yaw -= deltaX * 0.0052;
    this.pitch = applyVerticalLookDelta(this.pitch, deltaY, 0.0046);
  }

  triggerTouchAction(action: "jump" | "interact" | "glide", pressed: boolean): void {
    if (!this.enabled || this.paused) return;
    if (action === "jump" && pressed) this.jumpQueued = true;
    if (action === "interact" && pressed) this.callbacks.onInteract();
    if (action === "glide") this.glidePressed = pressed;
  }

  update(delta: number, elapsed: number): void {
    if (this.paused || !this.enabled) {
      this.updateCamera(delta, false);
      return;
    }

    const now = performance.now() / 1000;
    const previous = new THREE.Vector2(this.object.position.x, this.object.position.z);
    const keyboardX = Number(this.keys.has("KeyD") || this.keys.has("ArrowRight"))
      - Number(this.keys.has("KeyA") || this.keys.has("ArrowLeft"));
    const keyboardY = Number(this.keys.has("KeyW") || this.keys.has("ArrowUp"))
      - Number(this.keys.has("KeyS") || this.keys.has("ArrowDown"));
    const input = new THREE.Vector2(keyboardX + this.moveInput.x, keyboardY - this.moveInput.y);
    if (input.lengthSq() > 1) input.normalize();

    const forward = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    const right = new THREE.Vector3(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
    const moveDirection = forward.multiplyScalar(input.y).add(right.multiplyScalar(input.x));
    if (moveDirection.lengthSq() > 1) moveDirection.normalize();

    const sprinting = this.keys.has("ShiftLeft") || this.keys.has("ShiftRight");
    const targetSpeed = sprinting ? 11.2 : 7.2;
    const acceleration = this.grounded ? 34 : 13;
    const targetVelocityX = moveDirection.x * targetSpeed;
    const targetVelocityZ = moveDirection.z * targetSpeed;
    this.velocity.x = THREE.MathUtils.damp(this.velocity.x, targetVelocityX, acceleration, delta);
    this.velocity.z = THREE.MathUtils.damp(this.velocity.z, targetVelocityZ, acceleration, delta);

    const groundBefore = this.world.groundHeightAt(this.object.position.x, this.object.position.z);
    const distanceToGround = groundBefore === null ? Number.POSITIVE_INFINITY : this.object.position.y - groundBefore;
    if (groundBefore !== null && distanceToGround <= 0.14 && this.velocity.y <= 0) {
      this.object.position.y = groundBefore;
      this.velocity.y = 0;
      this.grounded = true;
      this.lastGroundedAt = now;
    } else {
      this.grounded = false;
    }

    const canJump = this.grounded || now - this.lastGroundedAt < 0.11;
    if (this.jumpQueued && canJump) {
      this.velocity.y = 9.3;
      this.grounded = false;
      this.jumpQueued = false;
    } else if (this.jumpQueued && now - this.lastGroundedAt > 0.22) {
      this.jumpQueued = false;
    }

    const wantsGlide = this.keys.has("KeyF") || this.glidePressed;
    const shouldGlide = wantsGlide && !this.grounded && this.velocity.y < 1.5;
    if (shouldGlide !== this.gliding) {
      this.gliding = shouldGlide;
      this.callbacks.onGlideChange(this.gliding);
    }

    if (!this.grounded) {
      const gravity = this.gliding ? -4.2 : -23.5;
      const terminalVelocity = this.gliding ? -4.4 : -33;
      this.velocity.y = Math.max(terminalVelocity, this.velocity.y + gravity * delta);
      if (this.gliding && moveDirection.lengthSq() > 0.05) {
        this.velocity.x = THREE.MathUtils.damp(this.velocity.x, moveDirection.x * 9.6, 5.5, delta);
        this.velocity.z = THREE.MathUtils.damp(this.velocity.z, moveDirection.z * 9.6, 5.5, delta);
      }
    }

    const desired = new THREE.Vector2(
      this.object.position.x + this.velocity.x * delta,
      this.object.position.z + this.velocity.z * delta,
    );
    const resolved = this.world.resolveMovement(previous, desired, this.object.position.y);
    if (Math.abs(resolved.x - desired.x) > 0.001) this.velocity.x = 0;
    if (Math.abs(resolved.y - desired.y) > 0.001) this.velocity.z = 0;
    this.object.position.x = resolved.x;
    this.object.position.z = resolved.y;
    this.object.position.y += this.velocity.y * delta;

    const groundAfter = this.world.groundHeightAt(this.object.position.x, this.object.position.z);
    if (groundAfter !== null && this.object.position.y <= groundAfter && this.velocity.y <= 0) {
      this.object.position.y = groundAfter;
      this.velocity.y = 0;
      this.grounded = true;
      this.lastGroundedAt = now;
      if (this.gliding) {
        this.gliding = false;
        this.callbacks.onGlideChange(false);
      }
    }

    if (moveDirection.lengthSq() > 0.04) {
      const facing = Math.atan2(-moveDirection.x, -moveDirection.z);
      this.limbs.body.rotation.y = this.dampAngle(this.limbs.body.rotation.y, facing, 12, delta);
    }

    this.animateCharacter(delta, elapsed, input.length(), sprinting);

    if (this.object.position.y < -48) this.respawn();
    this.updateCamera(delta, false);
  }

  private createBlockCharacter(): LimbSet {
    const body = new THREE.Group();
    const jacketMaterial = new THREE.MeshStandardMaterial({ color: 0x2f7180, roughness: 0.72 });
    const trimMaterial = new THREE.MeshStandardMaterial({ color: 0xd5a55f, roughness: 0.62, metalness: 0.12 });
    const trouserMaterial = new THREE.MeshStandardMaterial({ color: 0x344a55, roughness: 0.88 });
    const bootMaterial = new THREE.MeshStandardMaterial({ color: 0x3a2d27, roughness: 0.98 });
    const skinMaterial = new THREE.MeshStandardMaterial({ color: 0xd9a978, roughness: 0.8 });
    const hairMaterial = new THREE.MeshStandardMaterial({ color: 0x3f3028, roughness: 1 });

    const torso = new THREE.Mesh(new THREE.BoxGeometry(1.35, 1.65, 0.72), jacketMaterial);
    torso.position.y = 2.25;
    const belt = new THREE.Mesh(new THREE.BoxGeometry(1.42, 0.22, 0.78), trimMaterial);
    belt.position.y = 1.55;
    const head = new THREE.Mesh(new THREE.BoxGeometry(1.08, 1.08, 1.02), skinMaterial);
    head.position.y = 3.7;
    const hair = new THREE.Mesh(new THREE.BoxGeometry(1.14, 0.38, 1.08), hairMaterial);
    hair.position.set(0, 4.18, 0.02);
    const fringe = new THREE.Mesh(new THREE.BoxGeometry(1.12, 0.38, 0.18), hairMaterial);
    fringe.position.set(0, 3.96, -0.48);
    const eyeMaterial = new THREE.MeshBasicMaterial({ color: 0x172d35 });
    const leftEye = new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.13, 0.04), eyeMaterial);
    const rightEye = leftEye.clone();
    leftEye.position.set(-0.24, 3.74, -0.525);
    rightEye.position.set(0.24, 3.74, -0.525);

    const backpack = new THREE.Mesh(new THREE.BoxGeometry(1.05, 1.25, 0.45), trimMaterial);
    backpack.position.set(0, 2.28, 0.55);
    const core = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.22, 0),
      new THREE.MeshStandardMaterial({ color: 0x8bf5dc, emissive: 0x35bfa9, emissiveIntensity: 1.7 }),
    );
    core.position.set(0, 2.38, 0.82);

    const createArm = (side: number): THREE.Group => {
      const pivot = new THREE.Group();
      pivot.position.set(side * 0.87, 2.8, 0);
      const arm = new THREE.Mesh(new THREE.BoxGeometry(0.42, 1.5, 0.52), jacketMaterial);
      arm.position.y = -0.67;
      const hand = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.42, 0.54), skinMaterial);
      hand.position.y = -1.44;
      pivot.add(arm, hand);
      return pivot;
    };

    const createLeg = (side: number): THREE.Group => {
      const pivot = new THREE.Group();
      pivot.position.set(side * 0.37, 1.45, 0);
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.58, 1.25, 0.62), trouserMaterial);
      leg.position.y = -0.55;
      const boot = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.46, 0.84), bootMaterial);
      boot.position.set(0, -1.2, -0.08);
      pivot.add(leg, boot);
      return pivot;
    };

    const leftArm = createArm(-1);
    const rightArm = createArm(1);
    const leftLeg = createLeg(-1);
    const rightLeg = createLeg(1);
    const scarf = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.16, 1.8), new THREE.MeshStandardMaterial({ color: 0xb8543c, roughness: 0.92 }));
    scarf.position.set(0.28, 3.25, 0.78);
    scarf.rotation.x = -0.18;

    body.add(
      torso,
      belt,
      head,
      hair,
      fringe,
      leftEye,
      rightEye,
      backpack,
      core,
      leftArm,
      rightArm,
      leftLeg,
      rightLeg,
      scarf,
    );
    body.scale.setScalar(0.78);
    body.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });

    return { leftArm, rightArm, leftLeg, rightLeg, scarf, body };
  }

  private createGlider(): THREE.Group {
    const glider = new THREE.Group();
    glider.position.set(0, 3.1, 0.15);
    glider.rotation.x = 0.15;
    glider.visible = false;
    const frameMaterial = new THREE.MeshStandardMaterial({ color: 0x8e6944, roughness: 0.82 });
    const clothMaterial = new THREE.MeshStandardMaterial({
      color: 0xe6d3a2,
      roughness: 0.86,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.96,
    });

    const frame = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 6.7, 6), frameMaterial);
    frame.rotation.z = Math.PI / 2;
    glider.add(frame);

    const leftGeometry = new THREE.BufferGeometry();
    leftGeometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute([0, 0, 0, -3.3, 0.1, 0.3, -1.05, -0.2, 1.8], 3),
    );
    leftGeometry.setIndex([0, 1, 2]);
    leftGeometry.computeVertexNormals();
    const rightGeometry = new THREE.BufferGeometry();
    rightGeometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute([0, 0, 0, 3.3, 0.1, 0.3, 1.05, -0.2, 1.8], 3),
    );
    rightGeometry.setIndex([0, 2, 1]);
    rightGeometry.computeVertexNormals();
    const leftWing = new THREE.Mesh(leftGeometry, clothMaterial);
    const rightWing = new THREE.Mesh(rightGeometry, clothMaterial);
    glider.add(leftWing, rightWing);

    const stripeMaterial = new THREE.LineBasicMaterial({ color: 0xb7744b, transparent: true, opacity: 0.75 });
    glider.add(
      new THREE.Line(new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0.02, -0.03), new THREE.Vector3(-3.28, 0.12, 0.31)]), stripeMaterial),
      new THREE.Line(new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0.02, -0.03), new THREE.Vector3(3.28, 0.12, 0.31)]), stripeMaterial),
    );
    return glider;
  }

  private animateCharacter(delta: number, elapsed: number, inputAmount: number, sprinting: boolean): void {
    const movement = Math.hypot(this.velocity.x, this.velocity.z);
    this.walkCycle += delta * movement * (sprinting ? 1.35 : 1.05);
    const stride = this.grounded ? Math.sin(this.walkCycle) * Math.min(0.85, inputAmount * 0.85) : 0;
    this.limbs.leftLeg.rotation.x = THREE.MathUtils.damp(this.limbs.leftLeg.rotation.x, stride, 12, delta);
    this.limbs.rightLeg.rotation.x = THREE.MathUtils.damp(this.limbs.rightLeg.rotation.x, -stride, 12, delta);
    this.limbs.leftArm.rotation.x = THREE.MathUtils.damp(this.limbs.leftArm.rotation.x, -stride * 0.75, 12, delta);
    this.limbs.rightArm.rotation.x = THREE.MathUtils.damp(this.limbs.rightArm.rotation.x, stride * 0.75, 12, delta);
    this.limbs.body.position.y = this.grounded ? Math.abs(Math.sin(this.walkCycle * 2)) * Math.min(0.06, movement * 0.006) : 0;
    this.limbs.body.rotation.z = THREE.MathUtils.damp(this.limbs.body.rotation.z, -inputAmount * this.moveInput.x * 0.04, 8, delta);
    this.limbs.scarf.rotation.x = -0.22 - Math.min(0.42, movement * 0.025) + Math.sin(elapsed * 4.2) * 0.04;
    this.glider.visible = this.gliding;
    if (this.gliding) {
      this.limbs.leftArm.rotation.x = -1.3;
      this.limbs.rightArm.rotation.x = -1.3;
      this.limbs.leftArm.rotation.z = -0.6;
      this.limbs.rightArm.rotation.z = 0.6;
      this.glider.rotation.z = Math.sin(elapsed * 1.8) * 0.025;
    } else {
      this.limbs.leftArm.rotation.z = THREE.MathUtils.damp(this.limbs.leftArm.rotation.z, 0, 10, delta);
      this.limbs.rightArm.rotation.z = THREE.MathUtils.damp(this.limbs.rightArm.rotation.z, 0, 10, delta);
    }
  }

  private updateCamera(delta: number, immediate: boolean): void {
    const target = this.cameraTarget.set(
      this.object.position.x,
      this.object.position.y + 2.25,
      this.object.position.z,
    );
    const distance = this.gliding ? 9.3 : 7.6;
    const horizontalDistance = Math.cos(this.pitch) * distance;
    const ideal = new THREE.Vector3(
      target.x + Math.sin(this.yaw) * horizontalDistance,
      target.y + Math.sin(this.pitch) * distance + 1.05,
      target.z + Math.cos(this.yaw) * horizontalDistance,
    );

    const rayDirection = ideal.clone().sub(target);
    const idealDistance = rayDirection.length();
    rayDirection.normalize();
    this.raycaster.set(target, rayDirection);
    this.raycaster.near = 0.35;
    this.raycaster.far = idealDistance;
    const hits = this.raycaster.intersectObjects([...this.world.cameraColliders], false);
    if (hits.length > 0) {
      const safeDistance = Math.max(1.65, hits[0].distance - 0.35);
      ideal.copy(target).addScaledVector(rayDirection, safeDistance);
    }

    if (immediate) {
      this.camera.position.copy(ideal);
      this.cameraPosition.copy(ideal);
    } else {
      this.cameraPosition.lerp(ideal, 1 - Math.exp(-delta * 9));
      this.camera.position.copy(this.cameraPosition);
    }
    this.camera.lookAt(target);
  }

  private respawn(): void {
    this.object.position.copy(this.checkpoint);
    this.velocity.set(0, 0, 0);
    this.grounded = false;
    this.gliding = false;
    this.callbacks.onGlideChange(false);
    this.callbacks.onRespawn();
    this.updateCamera(1, true);
  }

  private bindInput(): void {
    window.addEventListener("keydown", (event) => {
      if (!this.enabled || this.paused) return;
      if (["Space", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.code)) event.preventDefault();
      this.keys.add(event.code);
      if (event.code === "Space" && !event.repeat) this.jumpQueued = true;
      if (event.code === "KeyE" && !event.repeat) this.callbacks.onInteract();
    });
    window.addEventListener("keyup", (event) => this.keys.delete(event.code));
    window.addEventListener("blur", () => this.clearInput());
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) this.clearInput();
    });
    document.addEventListener("mousemove", (event) => {
      if (document.pointerLockElement !== this.canvas || this.paused) return;
      this.yaw -= event.movementX * 0.00225;
      this.pitch = applyVerticalLookDelta(this.pitch, event.movementY, 0.0019);
    });
    document.addEventListener("pointerlockchange", () => {
      const locked = document.pointerLockElement === this.canvas;
      if (this.pointerWasLocked && !locked && this.enabled && !this.paused) this.callbacks.onPointerUnlock();
      this.pointerWasLocked = locked;
    });
    this.canvas.addEventListener("click", () => this.requestPointerLock());
    this.canvas.addEventListener("contextmenu", (event) => event.preventDefault());
    this.canvas.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "touch" || document.pointerLockElement === this.canvas) return;
      this.dragPointerId = event.pointerId;
      this.dragLastX = event.clientX;
      this.dragLastY = event.clientY;
      this.canvas.setPointerCapture(event.pointerId);
    });
    this.canvas.addEventListener("pointermove", (event) => {
      if (this.dragPointerId !== event.pointerId || this.paused) return;
      this.yaw -= (event.clientX - this.dragLastX) * 0.006;
      this.pitch = applyVerticalLookDelta(
        this.pitch,
        event.clientY - this.dragLastY,
        0.005,
      );
      this.dragLastX = event.clientX;
      this.dragLastY = event.clientY;
    });
    const endDrag = (event: PointerEvent): void => {
      if (this.dragPointerId === event.pointerId) this.dragPointerId = null;
    };
    this.canvas.addEventListener("pointerup", endDrag);
    this.canvas.addEventListener("pointercancel", endDrag);
  }

  private clearInput(): void {
    this.keys.clear();
    this.moveInput.set(0, 0);
    this.jumpQueued = false;
    this.glidePressed = false;
  }

  private dampAngle(current: number, target: number, smoothing: number, delta: number): number {
    let difference = (target - current + Math.PI) % (Math.PI * 2) - Math.PI;
    if (difference < -Math.PI) difference += Math.PI * 2;
    return current + difference * (1 - Math.exp(-smoothing * delta));
  }
}
